import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GOOGLE_COUNTRY_KEY } from "../components/GoogleSignIn";

// Emergent Google callback: session_id arrives in the URL hash (or query); exchanged once, server-side.
function readSessionId() {
  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
  return fromHash || new URLSearchParams(window.location.search).get("session_id");
}

export default function AuthCallback() {
  const processed = useRef(false);
  const [err, setErr] = useState("");
  const { loginWithToken } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = readSessionId();
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) { setErr("Missing sign-in session. Please try again."); return; }
    const country = sessionStorage.getItem(GOOGLE_COUNTRY_KEY) || undefined;
    sessionStorage.removeItem(GOOGLE_COUNTRY_KEY);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId, country_iso3: country });
        await loginWithToken(data.token);
        nav("/dashboard", { replace: true });
      } catch (e) {
        setErr(apiErr(e, "Google sign-in failed. Please try again."));
      }
    })();
  }, [loginWithToken, nav]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="auth-callback">
      {!err ? (
        <div className="flex flex-col items-center gap-3 text-slate-300" data-testid="auth-callback-pending">
          <Loader2 className="w-8 h-8 text-[#E5B842] animate-spin" />
          <div className="text-sm">Signing you in…</div>
        </div>
      ) : (
        <div className="rounded-xl bg-[#0f0f0f] border border-rose-500/30 p-6" data-testid="auth-callback-error">
          <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto" />
          <p className="text-rose-300 text-sm mt-3" data-testid="auth-callback-error-text">{err}</p>
          <Link to="/login" data-testid="auth-callback-back" className="inline-block mt-5 text-[#E5B842] font-bold text-sm uppercase tracking-wide">Back to login</Link>
        </div>
      )}
    </div>
  );
}
